import { Component } from '@theme/component';

/**
 * Feature tabs: swaps the panel, the section background and the colour scheme.
 *
 * Implements the ARIA tabs pattern rather than just wiring up clicks — roving
 * tabindex plus arrow/Home/End keys, so the strip is operable from the keyboard.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [tabList]
 * @property {HTMLElement[]} [panels]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class NoriTabs extends Component {
  #current = 0;

  /** @type {MutationObserver | undefined} */
  #panelObserver;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#handleKeydown);
    // A theme block's settings are not readable from section.blocks in Liquid,
    // so the markup cannot contain the tab strip. Each panel carries its own
    // label instead and the strip is built here.
    this.#buildTabs();
    // Panels render visible so the section is readable without JS; taking
    // ownership of that is the first thing the component does.
    this.#activate(0, { moveFocus: false });
    this.#watchPanels();
  }

  /**
   * The theme editor can re-render a single block rather than the whole
   * section, which would leave the JS-built strip showing stale labels — or
   * miss a newly added tab entirely. Watching the panels keeps the strip
   * honest while someone is editing.
   */
  #watchPanels() {
    const container = this.#panels[0]?.parentElement;

    if (!container) return;

    this.#panelObserver = new MutationObserver(() => this.#rebuild());
    this.#panelObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      // Deliberately narrow: `hidden` and `aria-labelledby` are set by this
      // component, and observing them would re-trigger this observer forever.
      attributeFilter: ['data-label', 'data-background', 'data-scheme'],
    });
  }

  #rebuild() {
    const list = this.refs.tabList;

    if (!list) return;

    const panels = this.#panels;
    const active = Math.min(this.#current, Math.max(0, panels.length - 1));

    list.replaceChildren();
    this.#buildTabs();
    this.#activate(active, { moveFocus: false });
  }

  #buildTabs() {
    const list = this.refs.tabList;
    const panels = this.#panels;

    if (!list || !panels.length || list.childElementCount) return;

    for (const [index, panel] of panels.entries()) {
      const button = document.createElement('button');

      button.type = 'button';
      button.className = 'nori-tabs__tab';
      button.setAttribute('role', 'tab');
      button.id = `${panel.id}-tab`;
      button.setAttribute('aria-controls', panel.id);
      button.setAttribute('aria-selected', String(index === 0));
      button.tabIndex = index === 0 ? 0 : -1;
      button.textContent = panel.dataset.label || `Tab ${index + 1}`;
      button.addEventListener('click', () => this.#activate(index, { moveFocus: false }));

      // Panels are authored with aria-labelledby pointing at a tab that only
      // exists once this runs, so the link is completed here.
      panel.setAttribute('aria-labelledby', button.id);

      list.append(button);
    }

    this.updateRefs?.();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('keydown', this.#handleKeydown);
    this.#panelObserver?.disconnect();
  }

  /** A ref with `[]` yields an array, but only once there are 2+ matches. */
  #list(ref) {
    if (!ref) return [];
    return Array.isArray(ref) ? ref : [ref];
  }

  get #tabs() {
    const list = this.refs.tabList;
    return list ? /** @type {HTMLButtonElement[]} */ ([...list.querySelectorAll('[role="tab"]')]) : [];
  }

  /**
   * Queried from the DOM rather than read off refs: the base class updates
   * refs from its own MutationObserver, so during a rebuild triggered by a
   * panel being removed, refs can still hold the element that just left.
   */
  get #panels() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tabpanel"]')]);
  }

  /** @param {Event} event */
  select = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('[role="tab"]');
    const index = this.#tabs.indexOf(/** @type {HTMLButtonElement} */ (button));

    // Clicking already places focus, so moving it again would be redundant.
    if (index !== -1) this.#activate(index, { moveFocus: false });
  };

  next = () => this.#step(1);

  previous = () => this.#step(-1);

  /** Wraps at both ends, so neither arrow is ever a dead control. */
  #step(delta) {
    const count = this.#tabs.length;
    if (!count) return;
    // Focus stays on the arrow so it can be pressed repeatedly.
    this.#activate((this.#current + delta + count) % count, { moveFocus: false });
  }

  #activate(index, { moveFocus = true } = {}) {
    const tabs = this.#tabs;
    const panels = this.#panels;

    if (!tabs.length) return;

    this.#current = index;

    for (const [i, tab] of tabs.entries()) {
      const isActive = i === index;
      tab.setAttribute('aria-selected', String(isActive));
      // Roving tabindex: only the active tab is in the tab order, so Tab moves
      // past the strip rather than through every tab in it.
      tab.tabIndex = isActive ? 0 : -1;
    }

    for (const [i, panel] of panels.entries()) {
      panel.hidden = i !== index;
    }

    const panel = panels[index];

    if (panel) {
      const { background, scheme } = panel.dataset;

      if (background) this.style.setProperty('--nori-tabs-bg', background);

      this.classList.toggle('nori-tabs--dark', scheme === 'dark');
      this.classList.toggle('nori-tabs--light', scheme !== 'dark');
    }

    if (moveFocus) tabs[index]?.focus();
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const tabs = this.#tabs;
    const target = /** @type {HTMLElement} */ (event.target);

    if (!tabs.includes(/** @type {HTMLButtonElement} */ (target))) return;

    const count = tabs.length;
    let index = null;

    switch (event.key) {
      case 'ArrowRight':
        index = (this.#current + 1) % count;
        break;
      case 'ArrowLeft':
        index = (this.#current - 1 + count) % count;
        break;
      case 'Home':
        index = 0;
        break;
      case 'End':
        index = count - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(index);
  };
}

if (!customElements.get('nori-tabs-component')) {
  customElements.define('nori-tabs-component', NoriTabs);
}
